import { globalJson } from "global/global_json";
import type { CustomerBody } from "./emailCode";

/**
 * A pay-later reservation waiting for a returning renter's email code (1NOW-10770).
 *
 * The booking is not made before the code is right, so what it will be made from is kept here: the renter's
 * details (what AddCustomer is sent again with the code), the booking payload without its `customer` (the code's
 * answer gives that), and what the Reserved page shows while it waits. It sits in sessionStorage (this tab only,
 * one entry per company, never in the URL), so a reload of the code page does not lose it. It is dropped when the
 * booking is made, on "Use a different email", and when the car page opens again.
 */
export interface HeldBooking {
  customer: CustomerBody;
  /** POST /api/bookings/ without `customer`; `pay_later: true` is in it. */
  payload: any;
  car: { id?: number | string; name?: string; photo?: string | null; range: string };
  /** The insurance option wants a document before payment: the files do not survive a reload (see heldFiles). */
  proofRequired?: boolean;
  /** The car page this started on, without its step: where "Use a different email" goes back to. */
  returnTo: string;
  /** When another code may be asked for (ms), so the wait carries on after a reload. */
  resendAt?: number;
  /** Why no code works yet (asked again too soon). */
  error?: string;
}

const key = () => `held_booking:${globalJson.company_id}`;

/**
 * The proof-of-insurance files cannot go into sessionStorage; they wait here, in memory, for the page change. A
 * reload loses them, and the Reserved page says so instead of making a booking the insurance option refuses.
 */
let heldFiles: { file: File }[] = [];
export const takeHeldFiles = () => heldFiles;

export const holdBooking = (held: HeldBooking, files: { file: File }[] = []) => {
  heldFiles = files;
  try {
    sessionStorage.setItem(key(), JSON.stringify(held));
  } catch {
    // private mode or full: the Reserved page finds nothing held and says so
  }
};

export const readHeld = (): HeldBooking | null => {
  try {
    const held = JSON.parse(sessionStorage.getItem(key()) ?? "null");
    if (held?.customer?.email && held.payload && held.returnTo) return held;
  } catch {
    // unreadable: as if nothing were held
  }
  return null;
};

export const clearHeld = () => {
  heldFiles = [];
  try {
    sessionStorage.removeItem(key());
  } catch {
    // nothing kept
  }
};

/** The car page's own address without its step: a step is not a way in once the page has been left. */
export const carAddress = ({ pathname, search }: { pathname: string; search: string }) => {
  const q = new URLSearchParams(search);
  q.delete("step");
  return pathname + (q.toString() ? `?${q}` : "");
};
